import {
  Bleed,
  Box,
  Divider,
  IconButton,
  Icons,
  Input,
  Select,
  Stack,
  tokens,
  Typography,
} from '@cast/design-system';
import { useFormState } from 'react-final-form';

import { EMPTY_LABEL, KINDS } from '../../_constants';
import type { BuilderRule, FormValues, Kind, RuleType } from '../../_types';

import { BuilderPanel } from './BuilderPanel';
import { LabelExpressionEditor } from './LabelExpressionEditor';

import {
  isNodeRule as isNodeRuleUtil,
  isNodeRuleType,
  validateRule,
} from '../../_utils/builder';

const RULE_TYPE_OPTIONS: { value: RuleType; label: string }[] = [
  { value: 'evict-pod', label: 'Evict pod' },
  { value: 'evict-node', label: 'Evict node' },
  { value: 'protect-pod', label: 'Protect pod' },
  { value: 'protect-node', label: 'Protect node' },
  { value: 'aggressive-pod', label: 'Aggressive Mode for Pod' },
  { value: 'aggressive-node', label: 'Aggressive Mode for Node' },
];

const NODE_RULE_PLACEHOLDER = '--';

type RuleRowProps = {
  rule: BuilderRule;
  disabled: boolean;
  onChange: (rule: BuilderRule) => void;
  onRemove: () => void;
};

export const RuleRow = ({
  rule,
  disabled,
  onChange,
  onRemove,
}: RuleRowProps) => {
  const { submitFailed } = useFormState<FormValues>({
    subscription: { submitFailed: true },
  });

  const validation = submitFailed ? validateRule(rule) : undefined;
  const hasError = validation ? !validation.valid : false;

  const isNodeRule = isNodeRuleUtil(rule);

  const fieldsDisabled = disabled || !rule.ruleType;

  return (
    <BuilderPanel testId="evictor-rule-row" hasError={hasError}>
      <Stack
        gap={12}
        divider={
          <Bleed>
            <Divider />
          </Bleed>
        }
      >
        <Stack gap={4}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr 32px',
              gap: 8,
              alignItems: 'end',
            }}
          >
            <Select
              size="sm"
              options={RULE_TYPE_OPTIONS}
              optionLabel="label"
              optionValue="value"
              value={rule.ruleType}
              onChange={(_option, newRuleType) => {
                if (isNodeRuleType(newRuleType)) {
                  onChange({
                    ruleType: newRuleType,
                    labels: rule.labels?.length
                      ? rule.labels
                      : [{ ...EMPTY_LABEL }],
                  });
                } else {
                  onChange({ ...rule, ruleType: newRuleType });
                }
              }}
              disabled={disabled}
              inputProps={{
                label: 'Rule',
                placeholder: 'Select rule type',
              }}
              testId="evictor-rule-type-select"
            />
            <Input
              size="small"
              label="Namespace"
              value={rule.namespace}
              onChange={(event) =>
                onChange({
                  ...rule,
                  namespace: event.target.value,
                })
              }
              disabled={fieldsDisabled || isNodeRule}
              placeholder={isNodeRule ? NODE_RULE_PLACEHOLDER : undefined}
              testId="evictor-rule-namespace"
            />
            <Select<Kind, Kind>
              size="sm"
              options={KINDS}
              value={rule.kind}
              onChange={(_option, value) => onChange({ ...rule, kind: value })}
              disabled={fieldsDisabled || isNodeRule}
              inputProps={{
                label: 'Kind',
                placeholder: isNodeRule ? NODE_RULE_PLACEHOLDER : undefined,
              }}
              testId="evictor-rule-kind-select"
              endAdornment={
                rule.kind && (
                  <IconButton
                    size="sm"
                    onClick={() => onChange({ ...rule, kind: undefined })}
                    disabled={disabled}
                    testId="evictor-remove-kind-button"
                  >
                    <Icons.X />
                  </IconButton>
                )
              }
            />
            <Input
              size="small"
              label="Min replicas"
              type="number"
              value={rule.replicasMin}
              onChange={(event) => {
                onChange({
                  ...rule,
                  replicasMin: event.target.value
                    ? Number(event.target.value)
                    : undefined,
                });
              }}
              disabled={fieldsDisabled || isNodeRule}
              placeholder={isNodeRule ? NODE_RULE_PLACEHOLDER : undefined}
              testId="evictor-rule-min-replicas"
            />
            <IconButton
              size="sm"
              onClick={onRemove}
              disabled={disabled}
              testId="evictor-remove-rule-button"
            >
              <Icons.Trash />
            </IconButton>
          </Box>
          {validation?.ruleError && (
            <Typography variant="P10R" color={tokens.theme.fg.status.error}>
              {validation.ruleError}
            </Typography>
          )}
        </Stack>
        <LabelExpressionEditor
          labels={rule.labels || []}
          disabled={fieldsDisabled}
          requireAtLeastOneLabel={isNodeRule}
          onChange={(labels) => onChange({ ...rule, labels })}
          labelErrors={validation?.labelErrors}
        />
      </Stack>
    </BuilderPanel>
  );
};
